// components/settings/DataSharingSection.tsx
import React, { useEffect, useState } from 'react';
import { telemetryAPI } from '../../api/client';
import { captureEvent, upgradePostHog, shutdownPostHog } from '../../lib/posthog';

const TIERS = [
  { value: 0, label: 'Off', description: 'Nothing leaves this device.' },
  { value: 1, label: 'Anonymous metrics', description: 'Usage stats & AI performance. No message content.' },
  { value: 2, label: 'Include AI content', description: 'Also share AI prompts & outputs to help improve quality. Phone numbers & names always removed.' },
];

const TIER_DETAILS: Record<number, string[]> = {
  0: ['All events stored locally in your database', 'Nothing is sent externally', 'You can query your own analytics via the API'],
  1: ['Feature usage (which features you use, not what you type)', 'AI performance metrics (speed, tokens, cost)', 'System health (memory, uptime, version)', 'Session replay with all text masked', 'No message content, contact names, or phone numbers'],
  2: ['Everything in Anonymous, plus:', 'AI-generated text (drafts, move descriptions)', 'AI prompts and completions', 'All phone numbers and names are scrubbed before sending'],
};

export default function DataSharingSection() {
  const [tier, setTier] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    telemetryAPI.getConfig()
      .then((c) => setTier(c.sharing_tier))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleChange(newTier: number) {
    const oldTier = tier;
    setSaving(true);
    try {
      const result = await telemetryAPI.updateConfig(newTier);
      captureEvent('telemetry_tier_changed', { from_tier: oldTier, to_tier: result.sharing_tier });
      setTier(result.sharing_tier);
      if (result.sharing_tier >= 1) {
        await upgradePostHog();
      } else {
        shutdownPostHog();
      }
    } catch {
      setTier(oldTier);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-ink" data-ph-no-mask>Data Sharing</h3>
      <div className="flex flex-col gap-2">
        {TIERS.map((t) => (
          <label
            key={t.value}
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              tier === t.value ? 'border-accent bg-accent/5' : 'border-edge'
            } ${saving ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <input type="radio" name="sharing-tier" value={t.value}
              checked={tier === t.value} onChange={() => handleChange(t.value)} className="mt-0.5" />
            <div>
              <span className="text-sm font-medium text-ink" data-ph-no-mask>{t.label}</span>
              <p className="text-xs text-ink-secondary mt-0.5" data-ph-no-mask>{t.description}</p>
            </div>
          </label>
        ))}
      </div>
      <button
        onClick={() => setShowDetail(!showDetail)}
        className="text-xs text-accent font-medium text-left"
        data-ph-no-mask
      >
        {showDetail ? 'Hide details' : 'What data is shared?'}
      </button>
      {showDetail && (
        <div className="p-3 rounded-lg bg-canvas-secondary text-xs text-ink-secondary">
          <ul className="list-disc pl-4 space-y-1">
            {TIER_DETAILS[tier]?.map((item, i) => (
              <li key={i} data-ph-no-mask>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
